import { action, makeAutoObservable } from 'mobx';

import { api } from '../services/api';
import STORAGE from '../services/storage';
import { userStore } from './userStore';

const STORAGE_KEY = 'KEIKKARI_SCOPE_V1';

const toScopeValue = (scope: IKeikkariScopeOption) => (scope.kind === 'logical' ? `logical:${scope.logicalTeamKey}` : `team:${scope.teamId}`);

class KeikkariScopeStore {
  status: 'IDLE' | 'BUSY' | 'FETCHED' | 'ERROR' = 'IDLE';
  options: IKeikkariScopeOption[] = [];
  selectedValue?: string;
  loadedForTeamId?: number;
  error?: any;

  constructor() {
    const saved = STORAGE.read({ key: STORAGE_KEY, target: 'session' });
    this.selectedValue = saved?.selectedValue;
    makeAutoObservable(this);
  }

  get currentTeamId() {
    return userStore.user?.teamId || userStore.user?.team?.likeitEmployeeId;
  }

  get fallbackScope(): IKeikkariScopeOption | undefined {
    const team = userStore.user?.team;
    if (!team) {
      return undefined;
    }

    return {
      kind: 'team',
      teamId: this.currentTeamId,
      name: team.name,
    };
  }

  get selectedScope(): IKeikkariScopeOption | undefined {
    return this.options.find(scope => toScopeValue(scope) === this.selectedValue) || this.fallbackScope;
  }

  get label() {
    return this.selectedScope?.name || userStore.user?.team?.name || '';
  }

  get selectOptions(): SelectOption<string>[] {
    return this.options.map(scope => ({
      label: scope.name,
      value: toScopeValue(scope),
    }));
  }

  get selectedOption(): SelectOption<string> | undefined {
    const scope = this.selectedScope;
    if (!scope) {
      return undefined;
    }

    return {
      label: scope.name,
      value: toScopeValue(scope),
    };
  }

  get shiftScopeParams(): { teamId?: number; logicalTeamKey?: string } {
    const scope = this.selectedScope;
    if (!scope) {
      return {};
    }

    return scope.kind === 'logical' ? { logicalTeamKey: scope.logicalTeamKey } : { teamId: scope.teamId };
  }

  get organizationScopeParams(): { likeitSellerPersonId?: number; logicalTeamKey?: string } {
    const scope = this.selectedScope;
    if (!scope) {
      return {};
    }

    return scope.kind === 'logical' ? { logicalTeamKey: scope.logicalTeamKey } : { likeitSellerPersonId: scope.teamId };
  }

  get organizationTreeScopeParams(): { SellerPersonId?: number; logicalTeamKey?: string } {
    const scope = this.selectedScope;
    if (!scope) {
      return {};
    }

    return scope.kind === 'logical' ? { logicalTeamKey: scope.logicalTeamKey } : { SellerPersonId: scope.teamId };
  }

  @action
  async ensureLoaded() {
    const { token, user } = userStore;
    const currentTeamId = this.currentTeamId;
    if (!token || !user || !currentTeamId) {
      return;
    }

    if (this.loadedForTeamId === currentTeamId && this.options.length) {
      return;
    }

    this.status = 'BUSY';
    const result = await api.getKeikkariScopes({ token });
    if (result.ok && result.data) {
      const nextOptions = result.data.results.length ? result.data.results : this.fallbackScope ? [this.fallbackScope] : [];
      this.options = nextOptions;
      this.loadedForTeamId = currentTeamId;
      this.error = undefined;

      const saved = STORAGE.read({ key: STORAGE_KEY, target: 'session' });
      const savedScope = nextOptions.find(scope => toScopeValue(scope) === saved?.selectedValue);
      const currentTeamScope = nextOptions.find(scope => scope.kind === 'team' && scope.teamId === currentTeamId);
      this.selectedValue = savedScope ? toScopeValue(savedScope) : currentTeamScope ? toScopeValue(currentTeamScope) : nextOptions[0] ? toScopeValue(nextOptions[0]) : undefined;
      STORAGE.write({ key: STORAGE_KEY, target: 'session', value: { selectedValue: this.selectedValue } });
      this.status = 'FETCHED';
    } else {
      this.error = result.originalError;
      this.options = this.fallbackScope ? [this.fallbackScope] : [];
      this.loadedForTeamId = currentTeamId;
      this.selectedValue = this.options[0] ? toScopeValue(this.options[0]) : undefined;
      this.status = 'ERROR';
    }
  }

  @action
  setSelected(scopeValue?: string | null) {
    if (!scopeValue) {
      return;
    }

    this.selectedValue = scopeValue;
    STORAGE.write({ key: STORAGE_KEY, target: 'session', value: { selectedValue: scopeValue } });
  }
}

export const keikkariScopeStore = new KeikkariScopeStore();
